import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { Button } from '../components/ui/Button.js';
import { VelocityLogo } from '../components/VelocityLogo.js';
import { Field, TextInput } from '../components/ui/Field.js';
import { authApi } from '../lib/api.js';
import { errorMessage, fieldErrorsOf } from '../lib/errors.js';
import { SESSION_KEY, useSession } from '../lib/session.js';

const ENTRA_ERRORS: Record<string, string> = {
  state_mismatch: 'The sign-in attempt expired. Try again.',
  access_denied: 'Entra ID declined the sign-in.',
  not_configured: 'Entra ID sign-in is not configured on this instance.',
  no_account: 'That account has no access to Velocity Alerts Manager.',
  account_disabled: 'That account is deactivated.',
  account_conflict:
    'Your email belongs to an account linked to a different Entra ID identity. Ask an owner to review it.',
  email_conflict:
    'The email Entra ID presented already belongs to another account. Ask an owner to review it.',
};

/**
 * Sign-in: Entra ID (a full-page redirect the SPA cannot follow with fetch)
 * and the local admin fallback.
 */
export function LoginPage() {
  const client = useQueryClient();
  const session = useSession();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Same-origin paths only: a protocol-relative `//host` makes <Navigate> throw
  // and blanks the page, and a crafted link must never leave the console.
  const rawReturnTo = params.get('return_to') ?? '/';
  const returnTo = /^\/(?!\/)/.test(rawReturnTo) ? rawReturnTo : '/';
  const oidcError = params.get('error');

  const login = useMutation({
    mutationFn: () => authApi.localLogin({ email: email.trim(), password }),
    onSuccess: (me) => {
      client.setQueryData(SESSION_KEY, me);
      void client.invalidateQueries();
    },
  });

  if (session.data && session.isSuccess) return <Navigate to={returnTo} replace />;

  const fieldErrors = fieldErrorsOf(login.error);

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    login.mutate();
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[var(--canvas)] px-4 py-12">
      {/*
        A quiet backdrop rather than an empty field: a hairline grid in the
        border token, faded out towards the edges. No gradient, no imagery, and
        nothing that competes with the card.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          backgroundImage:
            'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 35%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 60% at 50% 40%, #000 35%, transparent 100%)',
        }}
      />

      <div className="relative w-full max-w-[380px]">
        <div className="mb-6 flex flex-col items-center gap-2.5 text-center">
          <VelocityLogo height={44} className="text-[var(--text)]" />
          <div>
            <h1 className="text-[var(--text-xl)] font-semibold tracking-[-0.015em] text-[var(--text)]">
              Alerts Manager
            </h1>
            <p className="mt-0.5 text-[var(--text-base)] text-[var(--text-2)]">
              Sign in to the operations console.
            </p>
          </div>
        </div>

        <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--shadow-2)]">
          {oidcError && (
            <p
              role="alert"
              className="mb-4 rounded-[var(--radius-sm)] border border-[var(--danger)] bg-[var(--danger-soft)] px-3 py-2 text-[var(--text-base)] text-[var(--danger)]"
            >
              {ENTRA_ERRORS[oidcError] ?? `Sign-in failed (${oidcError}).`}
            </p>
          )}

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => {
              window.location.href = authApi.entraLoginUrl(returnTo);
            }}
          >
            Continue with Microsoft Entra ID
          </Button>

          <div className="my-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-[var(--border)]" />
            <span className="text-[var(--text-sm)] text-[var(--text-3)]">or local account</span>
            <span className="h-px flex-1 bg-[var(--border)]" />
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <Field label="Email" error={fieldErrors.email} required>
              {({ id, invalid }) => (
                <TextInput
                  id={id}
                  type="email"
                  autoComplete="username"
                  required
                  invalid={invalid}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="admin@example.com"
                />
              )}
            </Field>
            <Field label="Password" error={fieldErrors.password} required>
              {({ id, invalid }) => (
                <TextInput
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  required
                  invalid={invalid}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </Field>

            {login.error && Object.keys(fieldErrors).length === 0 && (
              <p role="alert" className="text-[var(--text-base)] text-[var(--danger)]">
                {errorMessage(login.error)}
              </p>
            )}

            <Button
              type="submit"
              variant="secondary"
              size="lg"
              className="mt-1 w-full"
              loading={login.isPending}
              disabled={!email.trim() || !password}
            >
              Sign in
            </Button>
          </form>
        </div>

        <p className="mx-auto mt-4 max-w-[300px] text-center text-[var(--text-sm)] text-[var(--text-3)]">
          Trouble signing in? Ask an owner to check your Entra group mapping.
        </p>
      </div>
    </div>
  );
}
